import React from 'react';
import { useIsDark } from '@/hooks/useIsDark';
import { cn } from '@/utils/cn';
import logoOnDark from '@/assets/brand/roofvox-logo-on-dark.png';
import logoOnLight from '@/assets/brand/roofvox-logo-on-light.png';
import logoMark from '@/assets/brand/roofvox-mark.png';

// Intrinsic artwork sizes (px): the full lockup is 883x158, the roof + waveform mark alone 297x158.
const LOCKUP_ASPECT = 883 / 158;
const MARK_ASPECT = 297 / 158;

export interface BrandLogoProps {
  /**
   * The surface the logo sits on. `dark` uses the lockup with a white wordmark, `light` the one with a navy
   * wordmark, `auto` follows the current colour mode. Pick by the surface behind the logo, not the mode:
   * the app sidebar and auth panels are dark in both modes.
   */
  surface?: 'dark' | 'light' | 'auto';
  /** `full` = roof mark + wordmark. `mark` = roof and waveform only, for tight spaces. */
  variant?: 'full' | 'mark';
  /** Rendered height in px; width follows the artwork's aspect ratio. Ignored when `fluid`. */
  height?: number;
  /** Size it with classes instead (e.g. `h-10 xl:h-14`), so it can scale by breakpoint. */
  fluid?: boolean;
  className?: string;
  alt?: string;
}

/** The platform (RoofVox) logo. A tenant's own uploaded logo is a different thing and lives in the top bar. */
const BrandLogo: React.FC<BrandLogoProps> = ({
  surface = 'auto',
  variant = 'full',
  height = 32,
  fluid = false,
  className,
  alt = 'RoofVox',
}) => {
  const isDark = useIsDark();
  const onDark = surface === 'dark' || (surface === 'auto' && isDark);
  const src = variant === 'mark' ? logoMark : onDark ? logoOnDark : logoOnLight;
  const aspect = variant === 'mark' ? MARK_ASPECT : LOCKUP_ASPECT;

  return (
    <img
      src={src}
      alt={alt}
      height={height}
      width={Math.round(height * aspect)}
      draggable={false}
      className={cn('block max-w-full object-contain', fluid && 'w-auto', className)}
      style={fluid ? undefined : { height, width: 'auto' }}
    />
  );
};

export default BrandLogo;
